import type { FAQs } from '@/types/referAndEarn';

export const musicFaqContent: FAQs = [
  {
    question: 'Is Uppbeat music royalty-free?',
    answer:
      "Yes – Uppbeat music is royalty-free. That means when you download tracks from the Uppbeat library, you can use them in your projects without paying ongoing royalties or additional fees, as long as you follow Uppbeat's licensing terms. This makes it easy to incorporate high quality music into videos, streams, podcasts, and other creative works with clarity and confidence.",
  },
  {
    question: 'What is royalty-free music?',
    answer:
      "Royalty-free music is music that you can use in your content without having to pay ongoing royalties to the artist. Each time you download a track from Uppbeat, you're licensed to use it in a single piece of content, e.g a video, podcast episode, social media posts, and more! It's a simple and affordable way to access high quality music while keeping your projects copyright-safe.",
  },
  {
    question: 'Do I need a license for Uppbeat music?',
    answer: `You don't need to download or sign a separate license document for Uppbeat music.

Our artists agree to the usage of their music in line with our usage policy. You may have heard of the term "pre-cleared" music, this is what it's referring to. When you create an Uppbeat account, you agree to our music usage policy which explains your coverage, similar to how a separate license would. For YouTube, your download also comes with an Uppbeat Credit you can paste into your video description to help prevent copyright claims (or you can safelist your channel if you're on a paid plan).`,
  },
  {
    question: 'Can I use Uppbeat music to make my own songs?',
    answer: `No – you can't use Uppbeat music to create and release your own songs.

Uppbeat tracks are licensed for syncing with content (like videos, podcasts, streams, and social posts), not for making "derivative works" and releasing them as new music. That means you can't:

- Sing or rap over an Uppbeat track and release it as your own song
- Release the track as-is as your own track
- Sample an Uppbeat track in your own music

You *can* make light edits when you're using the track in your content – like cutting or looping sections to fit your video length, or adding minimal effects – as long as the original track stays recognisable.`,
  },
  {
    question: 'How do I avoid YouTube copyright claims?',
    answer: `Avoiding YouTube copyright claims is easy when you use Uppbeat music as all our artists' tracks are registered to YouTube's Content ID system. To avoid claims, you simply need to show YouTube that you have permission to use the track using one of two methods:

- If you have a free plan: Copy the unique Uppbeat Credit you get with each download and paste it into your YouTube video description. Each credit is valid for one video, so you'll need a new credit for every upload (even if you reuse the same track).
- If you're on a paid plan: Safelist your YouTube channel using our safelisting tool, and you can use Uppbeat music on that channel without adding credits to each video.`,
  },
  {
    question: 'Is Uppbeat music AI generated?',
    answer: `No – Uppbeat music isn't AI generated.

Our catalog is made up of tracks created by real artists and composers who keep ownership of their music, and we license their work for creators to use in content.`,
  },
  {
    question: 'Can I use Uppbeat music for my business?',
    answer: `Yes – you can use Uppbeat music for your business, you'll just need to choose the right plan for you.

- If you're a solo professional, or a freelancer doing client work, you'll need [Uppbeat Pro](/pricing).
- If you're a company, brand, or agency, you'll need [Uppbeat Business](/pricing), which covers company and client content, including standard paid social and online ads.`,
  },
  {
    question: 'Can I use Uppbeat music for client work?',
    answer: `Yes – you can use Uppbeat music for client work, as long as you're on the right plan.

- Freelancers and solo professionals: Uppbeat Pro covers your own and client projects, and includes paid advertising clearance, up to 10 safelisted YouTube channels, and studio-quality WAV downloads for music and sound effects.
- Companies, brands, and agencies: Uppbeat Business covers company and client content, including standard paid social and online ads, with the same extended features: up to 10 safelisted YouTube channels and studio-quality WAV downloads.

Our Essentials and Creator plans are for individual creators making content for their own channels, and don't cover client work.`,
  },
];
